import React, { useState } from 'react';
import PropTypes from 'prop-types';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import './CustomCalendar.scss';

const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const DateRangeCalendar = ({ initialStartDate, initialEndDate, onDateRangeSelect, onClose }) => {
    const [selectedStartDate, setSelectedStartDate] = useState(initialStartDate);
    const [selectedEndDate, setSelectedEndDate] = useState(initialEndDate);
    const [leftMonth, setLeftMonth] = useState(new Date().getMonth());
    const [leftYear, setLeftYear] = useState(new Date().getFullYear());
    const [rightMonth, setRightMonth] = useState(new Date().getMonth() + 1);
    const [rightYear, setRightYear] = useState(new Date().getFullYear());

    const CalendarMonth = ({ month, year, onPrevMonth, onNextMonth }) => {
        const daysArray = generateCalendarDays(month, year);

        return (
            <div className="calendar-month">
                <div className="month-header">
                    <div className="month-navigation">
                        <button className="nav-button" onClick={onPrevMonth}>
                            <KeyboardArrowLeftIcon />
                        </button>
                        <span>{`${monthNames[month]} ${year}`}</span>
                        <button className="nav-button" onClick={onNextMonth}>
                            <KeyboardArrowRightIcon />
                        </button>
                    </div>
                </div>
                <div className="weekdays-header">
                    {weekDays.map(day => (
                        <div key={day} className="weekday">{day}</div>
                    ))}
                </div>
                <div className="calendar-grid">
                    {daysArray.map((dayDate, index) => renderDay(dayDate, index, month))}
                </div>
            </div>
        );
    };

    const generateCalendarDays = (month, year) => {
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const startDayOfMonth = new Date(year, month, 1).getDay();
        const daysArray = [];

        // Previous month days
        const prevMonthDays = new Date(year, month, 0).getDate();
        for (let i = startDayOfMonth - 1; i >= 0; i--) {
            daysArray.push(new Date(year, month - 1, prevMonthDays - i));
        }

        // Current month days
        for (let day = 1; day <= daysInMonth; day++) {
            daysArray.push(new Date(year, month, day));
        }

        // Next month days
        const remainingDays = 42 - daysArray.length;
        for (let day = 1; day <= remainingDays; day++) {
            daysArray.push(new Date(year, month + 1, day));
        }

        return daysArray;
    };

    const renderDay = (dayDate, index, currentMonth) => {
        const isSelectedStart = selectedStartDate && 
            dayDate.toDateString() === selectedStartDate.toDateString();
        const isSelectedEnd = selectedEndDate && 
            dayDate.toDateString() === selectedEndDate.toDateString();
        const isInRange = selectedStartDate && selectedEndDate && 
            dayDate > selectedStartDate && dayDate < selectedEndDate;
        const isCurrentMonth = dayDate.getMonth() === currentMonth;

        return (
            <div
                key={index}
                className={`day 
                    ${isSelectedStart ? 'selected-start' : ''} 
                    ${isSelectedEnd ? 'selected-end' : ''} 
                    ${isInRange ? 'in-range' : ''} 
                    ${!isCurrentMonth ? 'other-month' : ''}`
                }
                onClick={() => handleDateClick(dayDate)}
            >
                {dayDate.getDate()}
            </div>
        );
    };

    const handleDateClick = (date) => {
        if (!selectedStartDate || (selectedStartDate && selectedEndDate)) {
            setSelectedStartDate(date);
            setSelectedEndDate(null);
        } else {
            if (date < selectedStartDate) {
                setSelectedStartDate(date);
                setSelectedEndDate(null);
            } else {
                setSelectedEndDate(date);
                onDateRangeSelect(selectedStartDate, date);
            }
        }
    };

    const handleNavigateLeftMonth = (increment) => {
        const newMonth = leftMonth + increment;
        
        if (newMonth > 11) {
            setLeftMonth(0);
            setLeftYear(leftYear + 1);
        } else if (newMonth < 0) {
            setLeftMonth(11);
            setLeftYear(leftYear - 1);
        } else {
            setLeftMonth(newMonth);
        }
    };
    
    const handleNavigateRightMonth = (increment) => {
        const newMonth = rightMonth + increment;
        
        if (newMonth > 11) {
            setRightMonth(0);
            setRightYear(rightYear + 1);
        } else if (newMonth < 0) {
            setRightMonth(11);
            setRightYear(rightYear - 1);
        } else {
            setRightMonth(newMonth);
        }
    };

    const Shortcuts = () => (
        <div className="shortcuts">
            <button onClick={() => handleShortcut('Today')}>Today</button>
            <button onClick={() => handleShortcut('Yesterday')}>Yesterday</button>
            <button onClick={() => handleShortcut('Last 7 days')}>Last 7 days</button>
            <button onClick={() => handleShortcut('Last 30 days')}>Last 30 days</button>
            <button onClick={() => handleShortcut('This month')}>This month</button>
            <button onClick={() => handleShortcut('Last month')}>Last month</button>
        </div>
    );

    const handleShortcut = (type) => {
        const today = new Date();
        let start, end;

        switch (type) {
            case 'Today':
                start = end = new Date();
                break;
            case 'Yesterday':
                start = end = new Date(today.setDate(today.getDate() - 1));
                break;
            case 'Last 7 days':
                end = new Date();
                start = new Date(today.setDate(today.getDate() - 6));
                break;
            case 'Last 30 days':
                end = new Date();
                start = new Date(today.setDate(today.getDate() - 29));
                break;
            case 'This month':
                start = new Date(today.getFullYear(), today.getMonth(), 1);
                end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
                break;
            case 'Last month':
                start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                end = new Date(today.getFullYear(), today.getMonth(), 0);
                break;
            default:
                return;
        }
        
        setSelectedStartDate(start);
        setSelectedEndDate(end);
        onDateRangeSelect(start, end);
    };

    return (
        <div className="date-range-picker">
            <Shortcuts />
            <div className="calendars-container">
                <div className="calendars">
                    <CalendarMonth 
                        month={leftMonth} 
                        year={leftYear}
                        onPrevMonth={() => handleNavigateLeftMonth(-1)}
                        onNextMonth={() => handleNavigateLeftMonth(1)}
                    />
                    <CalendarMonth 
                        month={rightMonth} 
                        year={rightYear}
                        onPrevMonth={() => handleNavigateRightMonth(-1)}
                        onNextMonth={() => handleNavigateRightMonth(1)}
                    />
                </div>
            </div>
        </div>
    );
};

DateRangeCalendar.propTypes = {
    initialStartDate: PropTypes.instanceOf(Date),
    initialEndDate: PropTypes.instanceOf(Date),
    onDateRangeSelect: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
};

export default DateRangeCalendar;